import PageLayout from 'components/common/PageLayout';
import { useAuth } from 'contexts/AuthContext';
import useTokenValidation from 'hooks/useTokenValidation';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import RecebimentoCompletoCard from 'components/dashboard/RecebimentoCompletoCard';
import ResumoCards from 'components/dashboard/ResumoCards';
import EmpresasResumoCards from 'components/dashboard/EmpresasResumoCards';
import GraficoEvolucaoMensal from 'components/dashboard/GraficoEvolucaoMensal';
import GraficoConsolidado from 'components/dashboard/GraficoConsolidado';
import InsightsTempoReal from 'components/dashboard/InsightsTempoReal';
import financeiroService from 'features/financeiro/services/financeiroService';
import consolidationService from 'services/consolidationService';
import NotificationToast from 'components/common/NotificationToast';
import { setDefaultCompanyContext, hasCompanyContext, getCompanyContext } from 'utils/setCompanyContext';
import { useDeduplicatedRequest } from 'hooks/useDeduplicatedRequest';
import CompanyStatusIndicator from 'components/common/CompanyStatusIndicator';
import { useReceivableTerm } from 'hooks/useReceivableTerm';

const Dashboard = () => {
    const { user } = useAuth();
    const term = useReceivableTerm();
    const selectedCompanyRef = useRef(null);
    // Validação automática de token
    useTokenValidation();
    
    
    // Definir contexto da empresa se não existir
    useEffect(() => {
        if (!hasCompanyContext()) {
            setDefaultCompanyContext();
        }
    }, []);
    
    const [resumoTitulos, setResumoTitulos] = useState({});
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [, setUltimaAtualizacao] = useState(new Date());
    const [dadosGrafico, setDadosGrafico] = useState([]);
    const [dadosGraficoConsolidado, setDadosGraficoConsolidado] = useState(null);
    const [dadosBrutos, setDadosBrutos] = useState([]);
    const [isConsolidated, setIsConsolidated] = useState(false);
    const [insightsTempoReal, setInsightsTempoReal] = useState([]);
    const [atualizacaoAutomatica] = useState(true);
    const [notification, setNotification] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [dadosConsolidados, setDadosConsolidados] = useState(null);
    const { executeRequest } = useDeduplicatedRequest();
    const [companyAccentColor, setCompanyAccentColor] = useState('#3b82f6');

    useEffect(() => {
        selectedCompanyRef.current = selectedCompany;
    }, [selectedCompany]);

    useEffect(() => {
        const computeAccentColor = () => {
            try {
                const ctx = getCompanyContext();
                if (ctx?.isGroup || ctx?.isConsolidated) {
                    return '#6366f1';
                }
                const companyId = ctx?.companies?.[0]?.id;
                const byId = user?.accessible_companies?.find((c) => c.id === companyId);
                if (byId?.primary_color) return byId.primary_color;
                const byName = user?.accessible_companies?.find((c) => c.name === ctx?.companies?.[0]?.name);
                return byName?.primary_color || '#3b82f6';
            } catch (error) {
                return '#3b82f6';
            }
        };

        const updateColor = () => setCompanyAccentColor(computeAccentColor());
        updateColor();

        const handleContextChange = () => updateColor();
        window.addEventListener('company-context-changed', handleContextChange);
        return () => {
            window.removeEventListener('company-context-changed', handleContextChange);
        };
    }, [user?.accessible_companies]);

    const ajustarCoresGrafico = useCallback((dadosGraficoEntrada) => {
        if (!dadosGraficoEntrada || !Array.isArray(dadosGraficoEntrada.empresas)) {
            return dadosGraficoEntrada;
        }

        const normalizarNome = (nome) => (nome || '').toString().toLowerCase().replace(/\s+/g, '');
        const coresPorEmpresa = new Map(
            (user?.accessible_companies || [])
                .filter((empresa) => empresa?.name && empresa?.primary_color)
                .map((empresa) => [normalizarNome(empresa.name), empresa.primary_color])
        );

        return {
            ...dadosGraficoEntrada,
            empresas: dadosGraficoEntrada.empresas.map((empresa) => {
                const nomeEmpresa = normalizarNome(empresa?.nome || empresa?.name);
                const corEmpresa = coresPorEmpresa.get(nomeEmpresa);
                if (!corEmpresa) {
                    return empresa;
                }
                return {
                    ...empresa,
                    cor: corEmpresa
                };
            })
        };
    }, [user?.accessible_companies]);
    
    // Função para obter a empresa selecionada do contexto
    const getSelectedCompanyName = useCallback(() => {
        try {
            const companyContext = getCompanyContext();
            if (companyContext?.companies && companyContext.companies.length > 0) {
                // Se é grupo consolidado, retornar "Horizon Group"
                if (companyContext.isGroup || companyContext.isConsolidated) {
                    return 'Horizon Group';
                }
                // Se é empresa individual, retornar o nome da empresa
                return companyContext.companies[0].name;
            }
        } catch (error) {
            console.warn('Erro ao obter empresa selecionada:', error);
        }
        return typeof user.company === 'string' ? user.company : user.company?.name || 'MOV Fibra';
    }, [user.company]);

    // Função para processar dados do gráfico consolidados
    const processarDadosGraficoConsolidados = useCallback((dadosGrafico) => {
        if (!dadosGrafico || !dadosGrafico.consolidado || !dadosGrafico.consolidado.dados) {
            return [];
        }

        // Usar dados consolidados para o gráfico principal
        return dadosGrafico.consolidado.dados.map((item, index) => {
            // Calcular crescimento em relação ao mês anterior
            let crescimento = 0;
            if (index > 0) {
                const valorAnterior = dadosGrafico.consolidado.dados[index - 1].valor_recebido || 0;
                if (valorAnterior > 0) {
                    crescimento = ((item.valor_recebido - valorAnterior) / valorAnterior) * 100;
                }
            }

            // Formatar mês
            const [ano, mes] = item.mes.split('-');
            const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
            const mesIndex = parseInt(mes, 10) - 1;
            const mesFormatado = (mesIndex >= 0 && mesIndex < 12) ? `${meses[mesIndex]}/${ano.slice(2)}` : item.mes;

            return {
                mes: mesFormatado,
                valorRecebido: parseFloat(item.valor_recebido) || 0,
                titulosRecebidos: parseInt(item.titulos_recebidos) || 0,
                crescimento: parseFloat(crescimento.toFixed(2))
            };
        });
    }, []);

    const carregarDadosIniciais = useCallback(async (isReload = false) => {
        if (!isReload) {
            setLoading(true);
        } else {
            setIsRefreshing(true);
        }

        try {
            // Verificar se deve usar consolidação baseado no contexto da empresa
            const companyContext = getCompanyContext();
            const isConsolidatedValue = companyContext?.isConsolidated || 
                                      companyContext?.isGroup || 
                                      (companyContext?.companies?.length > 1 && user?.can_view_group_data);
            
            // Atualizar estado de consolidação
            setIsConsolidated(isConsolidatedValue);
            
            if (isConsolidatedValue) {
                // Para consolidação, buscar dados reais
                try {
                    // Usar os nomes das empresas do contexto em vez dos IDs
                    const companyNames = companyContext.companies.map(company => company.name);
                    

                    const dadosConsolidados = await consolidationService.buscarDadosConsolidados(
                        companyNames,
                        {
                            data_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
                            data_fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
                        }
                    );
                    
                    
                    // Armazenar dados consolidados no estado
                    setDadosConsolidados(dadosConsolidados);
                    
                    setResumoTitulos({
                        total_titulos_emitidos: dadosConsolidados.total_titulos_emitidos,
                        valor_total_emitido: dadosConsolidados.valor_total_emitido,
                        total_titulos_recebidos: dadosConsolidados.total_titulos_recebidos,
                        valor_total_recebido: dadosConsolidados.valor_total_recebido,
                        total_titulos_a_receber: dadosConsolidados.total_titulos_a_receber,
                        valor_restante_a_receber: dadosConsolidados.valor_restante_a_receber
                    });
                    
                    // Gerar insights consolidados mais interessantes
                    const insightsConsolidados = [
                        {
                            titulo: 'Performance do Grupo',
                            descricao: `Taxa de recebimento: ${dadosConsolidados.percentual_recebido?.toFixed(1) || 0}%`,
                            valor: dadosConsolidados.percentual_recebido || 0,
                            formato: 'percentual',
                            cor: dadosConsolidados.percentual_recebido > 70 ? 'green' : dadosConsolidados.percentual_recebido > 50 ? 'orange' : 'red',
                            icone: 'trending-up'
                        },
                        {
                            titulo: 'Receita Consolidada',
                            descricao: `R$ ${dadosConsolidados.valor_total_emitido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                            valor: dadosConsolidados.valor_total_emitido,
                            formato: 'moeda',
                            cor: 'blue',
                            icone: 'dollar-sign'
                        },
                        {
                            titulo: term.pendingLabel,
                            descricao: `${dadosConsolidados.total_titulos_a_receber?.toLocaleString('pt-BR') || 0} ${term.pluralLower} aguardando`,
                            valor: dadosConsolidados.total_titulos_a_receber || 0,
                            formato: 'numero',
                            cor: 'orange',
                            icone: 'clock'
                        },
                        {
                            titulo: 'Valor em Aberto',
                            descricao: `R$ ${dadosConsolidados.valor_restante_a_receber?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '0,00'}`,
                            valor: dadosConsolidados.valor_restante_a_receber || 0,
                            formato: 'moeda',
                            cor: 'purple',
                            icone: 'file-text'
                        },
                        {
                            titulo: 'Empresas Ativas',
                            descricao: `${dadosConsolidados.empresas.length} empresas no grupo`,
                            valor: dadosConsolidados.empresas.length,
                            formato: 'numero',
                            cor: 'indigo',
                            icone: 'building'
                        },
                        {
                            titulo: 'Ticket Médio',
                            descricao: `R$ ${((dadosConsolidados.valor_total_emitido || 0) / (dadosConsolidados.total_titulos_emitidos || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                            valor: (dadosConsolidados.valor_total_emitido || 0) / (dadosConsolidados.total_titulos_emitidos || 1),
                            formato: 'moeda',
                            cor: 'emerald',
                            icone: 'trending-up'
                        }
                    ];
                    
                    setInsightsTempoReal(insightsConsolidados);
                    
                    // Armazenar dados do gráfico consolidado (estrutura completa com cores)
                    const dadosGraficoComCores = ajustarCoresGrafico(dadosConsolidados.dados_grafico);
                    setDadosGraficoConsolidado(dadosGraficoComCores);

                    // Processar dados do gráfico consolidados para o formato esperado (fallback)
                    const dadosGraficoProcessados = processarDadosGraficoConsolidados(dadosGraficoComCores);
                    setDadosGrafico(dadosGraficoProcessados);

                    // Popular dados brutos para o card de recebimento (sparkline)
                    // RecebimentoCompletoCard usa campos `quantidade_titulos_emitidos` e `quantidade_titulos_recebidos`.
                    // No modo consolidado temos `evolucao_mensal` com `titulos_emitidos` e `titulos_recebidos`.
                    const dadosBrutosConsolidados = (dadosConsolidados.evolucao_mensal || []).map(item => ({
                        mes: item.mes,
                        quantidade_titulos_emitidos: item.titulos_emitidos || 0,
                        quantidade_titulos_recebidos: item.titulos_recebidos || 0,
                        valor_total_emitido: item.valor_emitido || 0,
                        valor_total_recebido: item.valor_recebido || 0,
                        quantidade_titulos_pendentes: item.titulos_pendentes || 0,
                        valor_pendente: item.valor_pendente || 0
                    }));
                    setDadosBrutos(dadosBrutosConsolidados);
                    
                } catch (error) {
                    console.error('❌ Erro ao buscar dados consolidados:', error);
                    // Fallback para dados mock em caso de erro
                    setResumoTitulos({
                        total_titulos_emitidos: 0,
                        valor_total_emitido: 0,
                        total_titulos_recebidos: 0,
                        valor_total_recebido: 0,
                        total_titulos_a_receber: 0,
                        valor_restante_a_receber: 0
                    });
                    setDadosGrafico([]);
                    setInsightsTempoReal([]);
                }
            } else {
                // Usar dados individuais da empresa
                const params = {
                    empresa: getSelectedCompanyName(),
                    data_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
                    data_fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
                };

                // Buscar dashboard financeiro completo (recomendado) com deduplicação
                const dashboardCompletoResp = await executeRequest(
                    financeiroService.buscarDashboardCompleto,
                    '/n8n/financeiro/gerar-dashboard-completo',
                    params,
                    'GET',
                    150000
                );
                
                // Verificar se a resposta tem a estrutura esperada
                if (dashboardCompletoResp && dashboardCompletoResp.success && dashboardCompletoResp.data) {
                    // A API retorna {success: true, data: Array(1), ...}
                    // Precisamos acessar dashboardCompletoResp.data.data
                    let dashboardData;
                    if (dashboardCompletoResp.data && dashboardCompletoResp.data.data) {
                        if (Array.isArray(dashboardCompletoResp.data.data)) {
                            if (dashboardCompletoResp.data.data.length > 0) {
                                dashboardData = dashboardCompletoResp.data.data[0];
                            } else {
                                throw new Error('Array de dados vazio');
                            }
                        } else {
                            // Se data.data não é array, usar diretamente
                            dashboardData = dashboardCompletoResp.data.data;
                        }
                    } else {
                        throw new Error('Estrutura de dados inválida');
                    }
                    
                    // Verificar se dashboardData foi definido corretamente
                    if (!dashboardData) {
                        throw new Error('dashboardData não foi definido');
                    }
                    
                    // Extrair dados do resumo
                    const resumo = dashboardData.resumo;
                    
                    // Verificar se resumo existe e tem a estrutura esperada
                    if (!resumo || typeof resumo !== 'object') {
                        throw new Error('Resumo não encontrado ou não é objeto');
                    }
                    
                    // Garantir que todos os campos sejam preenchidos corretamente
                    const resumoProcessado = {
                        total_titulos_emitidos: parseInt(resumo.total_titulos_emitidos) || 0,
                        valor_total_emitido: parseFloat(resumo.valor_total_emitido) || 0,
                        total_titulos_recebidos: parseInt(resumo.total_titulos_recebidos) || 0,
                        valor_total_recebido: parseFloat(resumo.valor_total_recebido) || 0,
                        total_titulos_a_receber: parseInt(resumo.total_titulos_a_receber) || 0,
                        valor_restante_a_receber: parseFloat(resumo.valor_restante_a_receber) || 0
                    };
                   
                    setResumoTitulos(resumoProcessado);
                   
                    // Armazenar dados brutos para o gráfico de percentual
                    setDadosBrutos(dashboardData.dados || []);
                    
                    // Processar dados de evolução mensal para o gráfico
                    let dadosProcessados = [];
                    if (!dashboardData.dados || !Array.isArray(dashboardData.dados)) {
                        setDadosGrafico([]);
                    } else {
                        dadosProcessados = dashboardData.dados.map((item, index) => {
                            // Verificar se item.mes existe e tem formato válido
                            if (!item.mes || typeof item.mes !== 'string') {
                                return {
                                    mes: `Mês ${index + 1}`,
                                    valorRecebido: parseFloat(item.valor_total_recebido) || 0,
                                    titulosRecebidos: parseInt(item.quantidade_titulos_recebidos) || 0,
                                    crescimento: parseFloat(item.percentual_crescimento) || 0
                                };
                            }
                            
                            const [, mes] = item.mes.split('-');
                            const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                            
                            // Validar se o mês é válido
                            const mesIndex = parseInt(mes, 10) - 1;
                            const mesFormatado = (mesIndex >= 0 && mesIndex < 12) ? meses[mesIndex] : `Mês ${mes}`;
                            
                            return {
                                mes: mesFormatado,
                                valorRecebido: parseFloat(item.valor_total_recebido) || 0,
                                titulosRecebidos: parseInt(item.quantidade_titulos_recebidos) || 0,
                                crescimento: parseFloat(item.percentual_crescimento) || 0
                            };
                        });
                        
                        setDadosGrafico(dadosProcessados);
                    }
                   
                    // Processar insights da IA
                    if (dashboardData.insights) {
                        const insightsArray = dashboardData.insights.split('|').filter(insight => insight.trim());
                        // Remover a limitação .slice(0, 4) para mostrar todos os insights
                        const insightsProcessados = insightsArray.map((insight, index) => {
                            const cores = ['blue', 'green', 'purple', 'orange', 'indigo', 'pink']; // Adicionar mais cores
                            const icones = ['trending-up', 'bar-chart-3', 'pie-chart', 'activity', 'trending-up', 'check-circle']; // Adicionar mais ícones
                            
                            return {
                                titulo: `Insight ${index + 1}`,
                                descricao: insight.trim(),
                                valor: index + 1,
                                formato: 'texto',
                                cor: cores[index] || 'blue',
                                icone: icones[index] || 'trending-up'
                            };
                        });
                        
                        setInsightsTempoReal(insightsProcessados);
                    } else {
                        // Mantém os indicadores calculados e informa quando a análise por IA não foi gerada.
                        setInsightsTempoReal([
                            ...(dashboardData.ia && dashboardData.ia.status !== 'disponivel' ? [{
                                titulo: 'Análise por IA indisponível',
                                descricao: dashboardData.ia.message || 'Os dados financeiros continuam disponíveis.',
                                valor: 0,
                                formato: 'texto',
                                cor: 'blue',
                                icone: 'info'
                            }] : []),
                            {
                                titulo: `Total de ${term.plural}`,
                                descricao: `${resumo.total_titulos_emitidos?.toLocaleString('pt-BR') || 0} ${term.pluralLower}`,
                                valor: parseInt(resumo.total_titulos_emitidos) || 0,
                                formato: 'numero',
                                cor: 'blue',
                                icone: 'file-text'
                            },
                            {
                                titulo: 'Receita Total',
                                descricao: `R$ ${parseFloat(resumo.valor_total_emitido || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                                valor: parseFloat(resumo.valor_total_emitido) || 0,
                                formato: 'moeda',
                                cor: 'green',
                                icone: 'dollar-sign'
                            }
                        ]);
                    }
                   
                } else {
                    // Usar dados mock em caso de erro
                    setDadosGrafico([]);
                    setResumoTitulos({});
                    setInsightsTempoReal([]);
                }
            }

        } catch (error) {
            console.error('❌ Erro ao carregar dados iniciais:', error);
            
            // Usar dados mock em caso de erro
            setDadosGrafico([]);
            setResumoTitulos({});
            setInsightsTempoReal([]);
            
            // Mostrar notificação de erro se foi um reload
            if (isReload) {
                setNotification({
                    message: 'Erro ao atualizar dados. Tente novamente.',
                    type: 'error'
                });
            }
        } finally {
            setLoading(false);
            setIsRefreshing(false);
            setUltimaAtualizacao(new Date());
            
            // Mostrar notificação de sucesso se foi um reload
            if (isReload) {
                setNotification({
                    message: 'Dados atualizados com sucesso!',
                    type: 'success'
                });
            }
        }
    }, [ajustarCoresGrafico, executeRequest, getSelectedCompanyName, processarDadosGraficoConsolidados, term.pendingLabel, term.plural, term.pluralLower, user]);

    useEffect(() => {
        carregarDadosIniciais();
    }, [carregarDadosIniciais]);

    // Listener para mudanças na empresa selecionada
    useEffect(() => {
        const handleStorageChange = () => {
            const currentCompany = getSelectedCompanyName();
            if (currentCompany !== selectedCompanyRef.current) {
                setSelectedCompany(currentCompany);
                carregarDadosIniciais(true);
            }
        };

        const handleCompanyChanged = (event) => {
            const { company } = event.detail;
            
            // Atualizar empresa selecionada
            setSelectedCompany(company);
            
            // Recarregar dados baseado no novo contexto
            carregarDadosIniciais(true);
        };

        // Verificar empresa inicial
        const initialCompany = getSelectedCompanyName();
        setSelectedCompany(initialCompany);

        // Escutar mudanças no localStorage
        window.addEventListener('storage', handleStorageChange);
        
        // Escutar mudanças customizadas (quando a mudança acontece na mesma aba)
        window.addEventListener('company-changed', handleCompanyChanged);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('company-changed', handleCompanyChanged);
        };
    }, [carregarDadosIniciais, getSelectedCompanyName]);

    const isPageBusy = loading || isRefreshing;
    return (
        <PageLayout 
            showRefreshButton={true}
            customRefreshFunction={() => carregarDadosIniciais(true)}
            rootClassName="theme-bg"
            mainClassName="theme-text"
        >
            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
                {isRefreshing && (
                    <div className="sticky top-2 z-40 mb-4 flex justify-center">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-1 text-xs text-[color:var(--text-primary)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur">
                            <span className="inline-flex h-2 w-2 animate-pulse rounded-full" style={{ background: companyAccentColor }}></span>
                            Atualizando dados...
                        </div>
                    </div>
                )}
                <div className="space-y-10">
                    {/* Hero + Consolidação */}
                    <div className="space-y-4">
                        {(() => {
                            const companyContext = getCompanyContext();
                            const isConsolidated = companyContext?.isConsolidated || 
                                                 companyContext?.isGroup || 
                                                 (companyContext?.companies?.length > 1 && user?.can_view_group_data);
                            
                            if (isConsolidated) {
                                return (
                                    <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2.5 py-0.5 text-[11px] text-[color:var(--text-muted)]">
                                        <span className="inline-flex h-1.5 w-1.5 rounded-full" style={{ background: companyAccentColor }}></span>
                                        <span className="font-semibold tracking-wide">Consolidado</span>
                                        {companyContext?.companies?.length > 0 && (
                                            <span className="hidden sm:inline text-[color:var(--text-muted)]">
                                                - {companyContext.companies.length} empresas
                                            </span>
                                        )}
                                    </div>
                                );
                            }
                            return null;
                        })()}

                        <RecebimentoCompletoCard 
                            resumoTitulos={resumoTitulos} 
                            dadosBrutos={dadosBrutos}
                            user={user}
                            loading={isPageBusy}
                        />
                    </div>

                    {/* Empresas e Status - APARECE APENAS NO MODO CONSOLIDADO */}
                    {(() => {
                        const companyContext = getCompanyContext();
                        const isConsolidated = companyContext?.isConsolidated || 
                                             companyContext?.isGroup || 
                                             (companyContext?.companies?.length > 1 && user?.can_view_group_data);
                        
                        if (isConsolidated) {
                            return (
                                <div className="space-y-6">
                                    <div>
                                        <div className="text-xs font-semibold tracking-[0.25em] text-[color:var(--text-muted)] uppercase">
                                            Empresas
                                        </div>
                                        <div className="text-lg font-semibold text-[color:var(--text-primary)]">Visão por empresa</div>
                                    </div>
                                    <EmpresasResumoCards />
                                    {dadosConsolidados?.status_empresas && (
                                        <CompanyStatusIndicator 
                                            statusEmpresas={dadosConsolidados.status_empresas}
                                        />
                                    )}
                                </div>
                            );
                        }
                        return null;
                    })()}

                    {/* Resumo Geral */}
                    <div className="space-y-4">
                        <div>
                            <div className="text-xs font-semibold tracking-[0.25em] text-[color:var(--text-muted)] uppercase">
                                Resumo Geral
                            </div>
                            <div className="text-lg font-semibold text-[color:var(--text-primary)]">Indicadores principais</div>
                        </div>
                        <ResumoCards resumoTitulos={resumoTitulos} loading={isPageBusy} />
                    </div>
                     
                    {/* Evolução e Insights */}
                    <div className="space-y-4">
                        <div>
                            <div className="text-xs font-semibold tracking-[0.25em] text-[color:var(--text-muted)] uppercase">
                                Evolução
                            </div>
                            <div className="text-lg font-semibold text-[color:var(--text-primary)]">Análise mensal e insights</div>
                        </div>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div className="w-full h-full">
                                {isConsolidated && dadosGraficoConsolidado ? (
                                    <GraficoConsolidado dadosGrafico={dadosGraficoConsolidado} loading={isPageBusy} accentColor={companyAccentColor} />
                                ) : (
                                    <GraficoEvolucaoMensal dadosGrafico={dadosGrafico} loading={isPageBusy} accentColor={companyAccentColor} />
                                )}
                            </div>
                            <div className="w-full h-full">
                                <InsightsTempoReal 
                                    insightsTempoReal={insightsTempoReal} 
                                    atualizacaoAutomatica={atualizacaoAutomatica}
                                    loading={isPageBusy}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Notificações Toast */}
            {notification && (
                <NotificationToast
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}
        </PageLayout>
    );
}
export default Dashboard;
